/**
 * Componente de Tabela de Itens da Fila de Webhooks PGMQ
 * 
 * Exibe tabela com itens da fila, incluindo:
 * - ID da mensagem
 * - Tenant
 * - Telefone
 * - Status
 * - Tentativas
 * - Data de criação
 * - Payload expandível (clique na linha)
 * 
 * Filtros disponíveis:
 * - Provider (z-api, evolution, chatwoot, wuzapi, notificame)
 * - Tenant (lista dinâmica do banco)
 * - Tipo (incoming, routing, webhook)
 * - Tentativas (0, 1, 2, 3+)
 * - Busca por telefone
 * - Status (pendente, processando, arquivado, dlq)
 */

import { useState, Fragment, useMemo, useEffect } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, FileJson, Clock, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWebhookQueueItems, WebhookQueueType, WebhookQueueItem } from '@/hooks/useWebhookQueueMonitor';
import { supabase } from '@/integrations/supabase/client';
import { PaginationFooter } from '@/components/ui/pagination-footer';

interface WebhookQueueItemsTableProps {
  queue: WebhookQueueType;
}

// Mapeamento de labels de status para exibição
const statusLabels: Record<string, string> = {
  pending: 'Pendente',
  processing: 'Processando',
  archived: 'Arquivado',
  dlq: 'DLQ',
};

// Mapeamento de variantes de badge para cada status
const statusVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pending: 'outline',
  processing: 'secondary',
  archived: 'default',
  dlq: 'destructive',
};

// Mapeamento de cores para providers (consistente com Fila de Fluxos)
const providerColors: Record<string, string> = {
  'z-api': 'border-green-500 text-green-500',
  'evolution': 'border-blue-500 text-blue-500',
  'chatwoot': 'border-orange-500 text-orange-500',
  'wuzapi': 'border-purple-500 text-purple-500',
  'notificame': 'border-cyan-500 text-cyan-500',
};

// Lista de providers disponíveis
const PROVIDERS = [
  { value: 'z-api', label: 'Z-API' },
  { value: 'evolution', label: 'Evolution' },
  { value: 'chatwoot', label: 'Chatwoot' },
  { value: 'wuzapi', label: 'WuzAPI' },
  { value: 'notificame', label: 'Notificame' },
];

// Lista de tipos de fila
const QUEUE_TYPES = [
  { value: 'incoming', label: 'Incoming' },
  { value: 'routing', label: 'Routing' },
  { value: 'webhook', label: 'Webhook' },
];

/**
 * Renderiza badge do provider com cor específica
 */
function getProviderBadge(providerSlug: string | null | undefined) {
  const colorClass = providerColors[providerSlug || ''] || '';
  return (
    <Badge variant="outline" className={colorClass}>
      {providerSlug?.toUpperCase() || 'N/A'}
    </Badge>
  );
}

/**
 * Formata o tempo de processamento em formato legível
 */
function formatProcessingTime(seconds: number | undefined): string {
  if (seconds === undefined || seconds === null) return '-';
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

/**
 * Componente de linha expansível para item da fila de webhook
 * Exibe payload JSON e timestamps detalhados
 */
function WebhookQueueItemRow({ item }: { item: WebhookQueueItem }) {
  const [isOpen, setIsOpen] = useState(false);
  
  // Verificar se há payload para exibir
  const hasPayload = item.message && Object.keys(item.message).length > 0;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
      <Fragment>
        {/* Linha principal da tabela */}
        <TableRow className={cn('hover:bg-muted/30 cursor-pointer', isOpen && 'bg-muted/20')}>
          <TableCell className="font-mono text-xs">
            {item.msg_id}
          </TableCell>
          <TableCell>
            <Badge variant={statusVariants[item.status] || 'outline'}>
              {statusLabels[item.status] || item.status}
            </Badge>
          </TableCell>
          <TableCell>
            {getProviderBadge(item.provider_slug)}
          </TableCell>
          <TableCell className="text-xs">
            {item.queue_type || '-'}
          </TableCell>
          <TableCell className="font-medium">
            {item.tenant_name}
          </TableCell>
          <TableCell className="font-mono text-sm">
            {item.phone || '-'}
          </TableCell>
          <TableCell className="text-xs text-muted-foreground">
            {item.flow_name || '-'}
          </TableCell>
          <TableCell className="text-center">
            <span className={item.read_ct >= 3 ? 'text-red-600 font-bold' : ''}>
              {item.read_ct}/3
            </span>
          </TableCell>
          <TableCell className="text-sm text-muted-foreground">
            {formatDistanceToNow(new Date(item.enqueued_at), { 
              addSuffix: true, 
              locale: ptBR 
            })}
          </TableCell>
          <TableCell className="text-sm font-mono">
            {formatProcessingTime(item.processing_time)}
          </TableCell>
          <TableCell>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                {isOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
          </TableCell>
        </TableRow>

        {/* Conteúdo expandido com payload e timestamps */}
        <CollapsibleContent asChild>
          <TableRow className="bg-muted/10 hover:bg-muted/10">
            <TableCell colSpan={11} className="p-0">
              <div className="p-4 space-y-4 border-t border-border/50">
                
                {/* Seção do Payload */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <FileJson className="h-4 w-4 text-primary" />
                    Payload
                  </div>
                  {hasPayload ? (
                    <pre className="text-xs bg-muted/50 p-3 rounded-md overflow-x-auto text-muted-foreground max-h-[300px] overflow-y-auto">
                      {JSON.stringify(item.message, null, 2)}
                    </pre>
                  ) : (
                    <p className="text-sm text-muted-foreground">Sem payload</p>
                  )}
                </div>

                {/* Timestamps detalhados */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Enfileirado: {format(new Date(item.enqueued_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}
                  </span>
                  {item.vt && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Visibility: {format(new Date(item.vt), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}
                    </span>
                  )}
                </div>

              </div>
            </TableCell>
          </TableRow>
        </CollapsibleContent>
      </Fragment>
    </Collapsible>
  );
}

/**
 * Barra de filtros para a tabela de itens
 * Componente separado para melhor organização
 */
interface WebhookQueueFiltersProps {
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  providerFilter: string;
  setProviderFilter: (value: string) => void;
  tenantFilter: string;
  setTenantFilter: (value: string) => void;
  typeFilter: string;
  setTypeFilter: (value: string) => void;
  attemptsFilter: string;
  setAttemptsFilter: (value: string) => void;
  phoneSearch: string;
  setPhoneSearch: (value: string) => void;
  tenants: { id: string; name: string }[];
}

function WebhookQueueFilters({
  statusFilter,
  setStatusFilter,
  providerFilter,
  setProviderFilter,
  tenantFilter,
  setTenantFilter,
  typeFilter,
  setTypeFilter,
  attemptsFilter,
  setAttemptsFilter,
  phoneSearch,
  setPhoneSearch,
  tenants,
}: WebhookQueueFiltersProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      {/* Filtro Provider */}
      <Select value={providerFilter} onValueChange={setProviderFilter}>
        <SelectTrigger className="w-[150px]">
          <SelectValue placeholder="Provider" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos Providers</SelectItem>
          {PROVIDERS.map((provider) => (
            <SelectItem key={provider.value} value={provider.value}>
              {provider.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Filtro Tenant */}
      <Select value={tenantFilter} onValueChange={setTenantFilter}>
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Tenant" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos Tenants</SelectItem>
          {tenants.map((tenant) => (
            <SelectItem key={tenant.id} value={tenant.id}>
              {tenant.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Filtro Tipo */}
      <Select value={typeFilter} onValueChange={setTypeFilter}>
        <SelectTrigger className="w-[140px]">
          <SelectValue placeholder="Tipo" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos Tipos</SelectItem>
          {QUEUE_TYPES.map((type) => (
            <SelectItem key={type.value} value={type.value}>
              {type.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Filtro Tentativas */}
      <Select value={attemptsFilter} onValueChange={setAttemptsFilter}>
        <SelectTrigger className="w-[140px]">
          <SelectValue placeholder="Tentativas" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todas</SelectItem>
          <SelectItem value="0">0 tentativas</SelectItem>
          <SelectItem value="1">1 tentativa</SelectItem>
          <SelectItem value="2">2 tentativas</SelectItem>
          <SelectItem value="3+">3+ tentativas</SelectItem>
        </SelectContent>
      </Select>

      {/* Filtro Status */}
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="w-[150px]">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos Status</SelectItem>
          <SelectItem value="pending">Pendentes</SelectItem>
          <SelectItem value="processing">Processando</SelectItem>
          <SelectItem value="archived">Arquivados</SelectItem>
          <SelectItem value="dlq">DLQ</SelectItem>
        </SelectContent>
      </Select>

      {/* Busca por telefone */}
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por telefone..."
          value={phoneSearch}
          onChange={(e) => setPhoneSearch(e.target.value)}
          className="w-[180px] pl-8"
        />
      </div>
    </div>
  );
}

/**
 * Componente principal da tabela de itens da fila de webhooks
 */
export function WebhookQueueItemsTable({ queue }: WebhookQueueItemsTableProps) {
  // Estados de filtro
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [attemptsFilter, setAttemptsFilter] = useState<string>('all');
  const [phoneSearch, setPhoneSearch] = useState<string>('');

  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Buscar dados da fila (limite aumentado para 200)
  const { data: items, isLoading } = useWebhookQueueItems(queue, statusFilter, 200);

  // Buscar lista de tenants para dropdown
  const { data: tenants = [] } = useQuery({
    queryKey: ['tenants-list-webhook'],
    queryFn: async () => {
      const { data } = await supabase
        .from('tenants')
        .select('id, name')
        .order('name');
      return data || [];
    },
  });

  // Filtragem E paginação no frontend com useMemo
  const { paginatedItems, totalFiltered } = useMemo(() => {
    if (!items) return { paginatedItems: [], totalFiltered: 0 };
    
    let filtered = items;
    
    // Filtro por provider
    if (providerFilter !== 'all') {
      filtered = filtered.filter(item => item.provider_slug === providerFilter);
    }
    
    // Filtro por tenant
    if (tenantFilter !== 'all') {
      filtered = filtered.filter(item => item.tenant_id === tenantFilter);
    }
    
    // Filtro por tipo
    if (typeFilter !== 'all') {
      filtered = filtered.filter(item => item.queue_type === typeFilter);
    }
    
    // Filtro por tentativas
    if (attemptsFilter !== 'all') {
      if (attemptsFilter === '3+') {
        filtered = filtered.filter(item => item.read_ct >= 3);
      } else {
        filtered = filtered.filter(item => item.read_ct === parseInt(attemptsFilter));
      }
    }
    
    // Filtro por telefone (busca parcial, só dígitos)
    if (phoneSearch.trim()) {
      const searchTerm = phoneSearch.replace(/\D/g, '');
      filtered = filtered.filter(item => {
        const phone = (item.phone || '').replace(/\D/g, '');
        return phone.includes(searchTerm);
      });
    }
    
    // Paginar itens filtrados
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, filtered.length);
    
    return {
      paginatedItems: filtered.slice(startIndex, endIndex),
      totalFiltered: filtered.length
    };
  }, [items, providerFilter, tenantFilter, typeFilter, attemptsFilter, phoneSearch, currentPage, pageSize]);

  // Resetar página ao mudar qualquer filtro
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, providerFilter, tenantFilter, typeFilter, attemptsFilter, phoneSearch]);

  // Estado de carregamento
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Itens da Fila</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          📋 Itens da Fila
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Barra de Filtros */}
        <WebhookQueueFilters
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          providerFilter={providerFilter}
          setProviderFilter={setProviderFilter}
          tenantFilter={tenantFilter}
          setTenantFilter={setTenantFilter}
          typeFilter={typeFilter}
          setTypeFilter={setTypeFilter}
          attemptsFilter={attemptsFilter}
          setAttemptsFilter={setAttemptsFilter}
          phoneSearch={phoneSearch}
          setPhoneSearch={setPhoneSearch}
          tenants={tenants}
        />

        {/* Tabela de Itens */}
        {!paginatedItems || paginatedItems.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Nenhum item encontrado na fila.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[70px]">ID</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[90px]">Provider</TableHead>
                  <TableHead className="w-[80px]">Tipo</TableHead>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead className="w-[100px]">Flow</TableHead>
                  <TableHead className="w-[80px]">Tentativas</TableHead>
                  <TableHead className="w-[100px]">Criado</TableHead>
                  <TableHead className="w-[70px]">Tempo</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedItems.map((item) => (
                  <WebhookQueueItemRow 
                    key={`${item.source}-${item.msg_id}`} 
                    item={item} 
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Paginação */}
        <PaginationFooter
          totalItems={totalFiltered}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          pageSizeOptions={[25, 50, 100, 200]}
        />
      </CardContent>
    </Card>
  );
}
